import { useState, useRef, useCallback, useEffect } from "react";

// Compress image to target size (approximately 500KB)
async function compressImage(blob, maxSizeKB = 500, maxDimension = 1600) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      let { width, height } = img;

      // Scale down if larger than max dimension
      if (width > maxDimension || height > maxDimension) {
        const ratio = Math.min(maxDimension / width, maxDimension / height);
        width = Math.round(width * ratio);
        height = Math.round(height * ratio);
      }

      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);

      // Try different quality levels
      let quality = 0.8;
      const tryCompress = () => {
        canvas.toBlob(
          (result) => {
            if (result.size > maxSizeKB * 1024 && quality > 0.3) {
              quality -= 0.1;
              tryCompress();
            } else {
              resolve(result);
            }
          },
          "image/jpeg",
          quality
        );
      };
      tryCompress();
    };
    img.src = URL.createObjectURL(blob);
  });
}

export default function PhotoCapture({ onPhotoCapture, onSkip }) {
  const [mode, setMode] = useState(null); // null | 'camera' | 'upload'
  const [cameraError, setCameraError] = useState(null);
  const [stream, setStream] = useState(null);
  const [capturing, setCapturing] = useState(false);
  const [preview, setPreview] = useState(null);
  const videoRef = useRef(null);
  const fileInputRef = useRef(null);

  // Cleanup stream on unmount
  useEffect(() => {
    return () => {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [stream]);

  const startCamera = useCallback(async () => {
    setCameraError(null);
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" }, // Prefer back camera
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });
      setStream(mediaStream);
      setMode("camera");
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
      }
    } catch (err) {
      console.error("Camera access error:", err);
      if (err.name === "NotAllowedError") {
        setCameraError("Camera permission denied. Please allow camera access or upload a photo.");
      } else if (err.name === "NotFoundError") {
        setCameraError("No camera found. Please upload a photo instead.");
      } else {
        setCameraError(`Camera error: ${err.message}`);
      }
      setMode("upload");
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
  }, [stream]);

  const capturePhoto = useCallback(async () => {
    if (!videoRef.current || capturing) return;
    setCapturing(true);

    try {
      const video = videoRef.current;
      const canvas = document.createElement("canvas");
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0);

      canvas.toBlob(async (blob) => {
        const compressed = await compressImage(blob);
        const url = URL.createObjectURL(compressed);
        setPreview(url);
        stopCamera();
        onPhotoCapture(url, compressed);
      }, "image/jpeg", 0.9);
    } catch (err) {
      console.error("Capture error:", err);
      setCapturing(false);
    }
  }, [capturing, stopCamera, onPhotoCapture]);

  const handleFileUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const compressed = await compressImage(file);
      const url = URL.createObjectURL(compressed);
      setPreview(url);
      onPhotoCapture(url, compressed);
    } catch (err) {
      console.error("Upload error:", err);
      setCameraError("Failed to process image. Please try another.");
    }
  }, [onPhotoCapture]);

  const retake = useCallback(() => {
    setPreview(null);
    setCapturing(false);
    if (mode === "camera") {
      startCamera();
    }
  }, [mode, startCamera]);

  // Initial mode selection
  if (mode === null) {
    return (
      <div style={{ padding: "20px 0" }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>{"\u{1F4F7}"}</div>
          <h3 style={{ margin: 0, color: "#F0F0F0", fontSize: 18, fontWeight: 700 }}>
            Add a Photo
          </h3>
          <p style={{ color: "#888", fontSize: 13, marginTop: 8 }}>
            Take a photo of your container so Claude can identify the items inside
          </p>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <button
            onClick={startCamera}
            style={{
              padding: "16px 24px",
              borderRadius: 12,
              border: "2px solid #F0C75E",
              background: "#F0C75E22",
              color: "#F0C75E",
              fontSize: 15,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
            }}
          >
            <span style={{ fontSize: 20 }}>{"\u{1F4F8}"}</span>
            Take Photo
          </button>

          <button
            onClick={() => {
              setMode("upload");
              fileInputRef.current?.click();
            }}
            style={{
              padding: "16px 24px",
              borderRadius: 12,
              border: "1px solid #333",
              background: "#1a1a3e",
              color: "#aaa",
              fontSize: 15,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
            }}
          >
            <span style={{ fontSize: 20 }}>{"\u{1F4C1}"}</span>
            Upload from Device
          </button>

          {onSkip && (
            <button
              onClick={onSkip}
              style={{
                padding: "12px 24px",
                borderRadius: 12,
                border: "none",
                background: "transparent",
                color: "#666",
                fontSize: 13,
                cursor: "pointer",
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              Skip for now
            </button>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleFileUpload}
          style={{ display: "none" }}
        />
      </div>
    );
  }

  // Preview mode
  if (preview) {
    return (
      <div style={{ padding: "20px 0" }}>
        <div
          style={{
            width: "100%",
            aspectRatio: "4/3",
            borderRadius: 16,
            overflow: "hidden",
            background: "#000",
            marginBottom: 16,
          }}
        >
          <img
            src={preview}
            alt="Captured"
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
          />
        </div>
        <div style={{ display: "flex", gap: 12 }}>
          <button
            onClick={retake}
            style={{
              flex: 1,
              padding: "12px",
              borderRadius: 10,
              border: "1px solid #333",
              background: "#1a1a3e",
              color: "#aaa",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            Retake
          </button>
        </div>
      </div>
    );
  }

  // Camera mode
  if (mode === "camera") {
    return (
      <div style={{ padding: "20px 0" }}>
        <div
          style={{
            width: "100%",
            aspectRatio: "4/3",
            borderRadius: 16,
            overflow: "hidden",
            background: "#000",
            marginBottom: 16,
            position: "relative",
          }}
        >
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
          {/* Capture button overlay */}
          <button
            onClick={capturePhoto}
            disabled={capturing}
            style={{
              position: "absolute",
              bottom: 20,
              left: "50%",
              transform: "translateX(-50%)",
              width: 70,
              height: 70,
              borderRadius: "50%",
              border: "4px solid #fff",
              background: capturing ? "#666" : "#F0C75E",
              cursor: capturing ? "not-allowed" : "pointer",
              boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
            }}
          />
        </div>
        <div style={{ display: "flex", gap: 12 }}>
          <button
            onClick={() => {
              stopCamera();
              setMode(null);
            }}
            style={{
              flex: 1,
              padding: "12px",
              borderRadius: 10,
              border: "1px solid #333",
              background: "#1a1a3e",
              color: "#aaa",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => fileInputRef.current?.click()}
            style={{
              flex: 1,
              padding: "12px",
              borderRadius: 10,
              border: "1px solid #333",
              background: "#1a1a3e",
              color: "#aaa",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            Upload Instead
          </button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileUpload}
          style={{ display: "none" }}
        />
      </div>
    );
  }

  // Upload mode (fallback)
  return (
    <div style={{ padding: "20px 0" }}>
      {cameraError && (
        <div
          style={{
            padding: "12px 16px",
            background: "#E8927C22",
            border: "1px solid #E8927C44",
            borderRadius: 10,
            color: "#E8927C",
            fontSize: 13,
            marginBottom: 16,
          }}
        >
          {cameraError}
        </div>
      )}

      <div
        onClick={() => fileInputRef.current?.click()}
        style={{
          width: "100%",
          aspectRatio: "4/3",
          borderRadius: 16,
          border: "2px dashed #333",
          background: "#12122a",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          marginBottom: 16,
        }}
      >
        <span style={{ fontSize: 48, marginBottom: 12 }}>{"\u{1F4C1}"}</span>
        <span style={{ color: "#888", fontSize: 14 }}>Tap to upload a photo</span>
      </div>

      <div style={{ display: "flex", gap: 12 }}>
        <button
          onClick={() => setMode(null)}
          style={{
            flex: 1,
            padding: "12px",
            borderRadius: 10,
            border: "1px solid #333",
            background: "#1a1a3e",
            color: "#aaa",
            fontSize: 14,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: "'DM Sans', sans-serif",
          }}
        >
          Back
        </button>
        <button
          onClick={startCamera}
          style={{
            flex: 1,
            padding: "12px",
            borderRadius: 10,
            border: "1px solid #333",
            background: "#1a1a3e",
            color: "#aaa",
            fontSize: 14,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: "'DM Sans', sans-serif",
          }}
        >
          Try Camera Again
        </button>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileUpload}
        style={{ display: "none" }}
      />
    </div>
  );
}
